import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import {
  AlertTriangle,
  Ban,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  Clock3,
  FileText,
  Filter,
  Lock,
  Pencil,
  Printer,
  Receipt,
  RefreshCw,
  Trash2,
  Undo2,
  Wallet,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import { CancelSaleDialog } from './CancelSaleDialog';
import {
  adjustQuantityByUnit,
  applyApolloErrorMessage,
  collectKitBreakdownTargets,
  customerLabel,
  dateTimeLabel,
  groupItemsByKit,
  kitBreakdownDisplayRows,
  kitComponentLabel,
  normalizeOptionalText,
  normalizeSaleOrder,
  parseWeightKgToGrams,
  petLabel,
  printSaleTicket,
  safeTicketBigInt,
  summarizePaymentMethodLines,
  ticketLabel,
  timeLabel,
  type OrderCustomerSnapshot,
  type OrderPetSnapshot,
  type SaleOrder,
  type SaleOrderItem,
  type TicketPaymentMethod,
} from './sale-order-shared';
import {
  APPLY_CASHIER_ORDER_DISCOUNT_MUTATION,
  CASHIER_SALE_ORDER_QUERY,
  CASHIER_CONSULTING_ROOMS_QUERY,
  CASHIER_SHIFT_SALE_HISTORY_QUERY,
  CHARGE_SALE_ORDER_MUTATION,
  CREATE_CASHIER_MEDICAL_ORDER_MUTATION,
  DISCARD_CASHIER_OPENING_ORDER_MUTATION,
  KIT_BREAKDOWN_LINES_BY_CODE_QUERY,
  KIT_BREAKDOWN_LINES_QUERY,
  PENDING_SALE_ORDERS_QUERY,
  ADD_CASHIER_SALE_LINE_MUTATION,
  REMOVE_CASHIER_SALE_LINE_MUTATION,
  REASSIGN_CASHIER_PENDING_CONSULTING_ROOM_MUTATION,
  RETURN_SALE_ORDER_TO_DOCTOR_MUTATION,
  SEND_CASHIER_MEDICAL_ORDER_TO_DOCTOR_MUTATION,
  UPDATE_CASHIER_MEDICAL_ORDER_MUTATION,
  UPDATE_CASHIER_SALE_LINE_MUTATION,
} from './cashier-pos.gql';
import {
  APPOINTMENTS_CHECKED_IN_QUERY,
  VETERINARIAN_USERS_QUERY,
} from '@/modules/appointments/appointments.gql';
import { CashierClinicalAdvisory } from '@/modules/clinical-records/CashierClinicalAdvisory';
import { ClinicalVisitModal } from '@/modules/clinical-records/ClinicalVisitModal';

import { PaymentPanel, type PaymentDraftValue } from './PaymentPanel';
import { ReturnToDoctorModal } from './ReturnToDoctorModal';
import { CustomerPetPicker } from '@/modules/doctor-pos/CustomerPetPicker';
import { SellableSearchPanel, type SellableHit } from '@/modules/doctor-pos/SellableSearchPanel';

import { RequestCfdiDialog } from '@/modules/invoicing/RequestCfdiDialog';
import { OpenAccountDetailDialog } from '@/modules/open-accounts/OpenAccountDetailDialog';
import { OPEN_ACCOUNTS_QUERY } from '@/modules/open-accounts/open-accounts.gql';
import { useAuthStore } from '@/stores/authStore';

import {
  MY_ACTIVE_CASH_SHIFT_QUERY,
  MY_CASH_SHIFT_STATUS_QUERY,
} from '@/modules/cash-shifts/cash-shifts.gql';
import { useActiveShiftStore } from '@/stores/activeShiftStore';
import {
  OPEN_ACCOUNT_STATUS_TO_GQL,
  OPEN_ACCOUNT_KIND_TO_GQL,
  OPEN_ACCOUNT_KIND_FROM_GQL,
} from '@/shared/graphql/enums';
import { formatCentsString } from '@/shared/lib/money';
import { cn } from '@/shared/lib/cn';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { ConfirmDialog } from '@/shared/ui/confirm-dialog';

interface AppointmentArrival {
  id: string;
  folio_number: string;
  sale_order_id: string | null;
  pet_name: string | null;
  veterinarian_id: string;
  scheduled_start_at: string;
}

interface PendingResponse {
  pendingSaleOrders: { items: SaleOrder[]; page: { total: number } };
}

interface ReassignCashierPendingConsultingRoomResponse {
  reassignCashierPendingConsultingRoom: {
    consulting_room_occupied: boolean;
    advisory_code: string | null;
    order: SaleOrder;
  };
}

interface CashierOrderResponse {
  cashierSaleOrder: SaleOrder;
}

interface CashierShiftSaleHistoryEntry {
  sale_order_id: string;
  sale_folio_number: string | null;
  ticket_id: string | null;
  ticket_folio_number: string | null;
  paid_at: string | null;
  total_cents: string;
  status: string;
  payment_method_summary: string | null;
  tutor_name: string | null;
  pet_name: string | null;
  customer_snapshot_json?: OrderCustomerSnapshot | string | null;
  pet_snapshot_json?: OrderPetSnapshot | string | null;
  payment_methods: TicketPaymentMethod[];
  consulting_room_name?: string | null;
  sale_order: SaleOrder;
}

interface CashierShiftSaleHistoryResponse {
  cashierShiftSaleHistory: CashierShiftSaleHistoryEntry[];
}

interface MyActiveShiftResponse {
  myActiveCashShift: {
    id: string;
    cash_register_id: string;
    branch_id: string;
    opened_at: string;
  } | null;
}

interface MyCashShiftStatusResponse {
  myCashShiftStatus: {
    has_own_open_shift: boolean;
    has_branch_open_shift: boolean;
    branch_open_shift_cashier_name: string | null;
  };
}

interface ConsultingRoomLite {
  id: string;
  name: string;
  display_order: number;
  zone_type: 'consulting_room' | 'xray' | 'ultrasound' | 'other';
  status: 'active' | 'inactive';
  branch_id: string;
  workstation_id: string | null;
}

interface ConsultingRoomsResponse {
  consultingRooms: { items: ConsultingRoomLite[] };
}

interface OpenAccountListItem {
  id: string;
  folio_number: string | null;
  customer_id: string;
  pet_id: string;
  kind: string;
  status: string;
  balance_cents: string;
  opened_at: string;
  notes: string | null;
  tutor_name: string | null;
  pet_name: string | null;
}

interface OpenAccountsResponse {
  openAccounts: { items: OpenAccountListItem[]; page: { total: number } };
}

export function CashierPosPage(): JSX.Element {
  const { t } = useTranslation();
  const apollo = useApolloClient();
  const [show_filters, setShowFilters] = useState(false);
  const [is_hospitalizations_collapsed, setIsHospitalizationsCollapsed] = useState(false);
  // Appointment-arrival orders live in their own panel, collapsed by default.
  const [is_citas_collapsed, setIsCitasCollapsed] = useState(true);
  const [is_sent_to_doctor_collapsed, setIsSentToDoctorCollapsed] = useState(false);
  const [is_opening_collapsed, setIsOpeningCollapsed] = useState(false);
  const [filter_folio, setFilterFolio] = useState('');
  const [filter_customer_id, setFilterCustomerId] = useState('');
  const [filter_vet_id, setFilterVetId] = useState('');

  const [search_params] = useSearchParams();
  const [selected_id, setSelectedId] = useState<string | null>(null);
  const [show_return, setShowReturn] = useState(false);
  const [show_cancel, setShowCancel] = useState(false);
  const [show_clinical_modal, setShowClinicalModal] = useState(false);
  const [show_discard_confirm, setShowDiscardConfirm] = useState(false);
  const [discard_target_id, setDiscardTargetId] = useState<string | null>(null);
  const [history_detail, setHistoryDetail] = useState<CashierShiftSaleHistoryEntry | null>(null);
  const [open_account_detail_id, setOpenAccountDetailId] = useState<string | null>(null);
  const [charged_order_ids, setChargedOrderIds] = useState<Set<string>>(() => new Set());
  const [last_paid_folio, setLastPaidFolio] = useState<string | null>(null);
  const [last_paid_sale, setLastPaidSale] = useState<{ id: string; customer_id: string } | null>(
    null,
  );
  const [show_request_cfdi, setShowRequestCfdi] = useState(false);
  const [error_text, setErrorText] = useState<string | null>(null);
  const [kit_breakdowns, setKitBreakdowns] = useState<Record<string, string[]>>({});
  const [reassign_room_by_order_id, setReassignRoomByOrderId] = useState<Record<string, string>>(
    {},
  );
  const [reassign_reason_by_order_id, setReassignReasonByOrderId] = useState<
    Record<string, string>
  >({});
  const [reassign_advisory_by_order_id, setReassignAdvisoryByOrderId] = useState<
    Record<string, string | null>
  >({});
  const [reassigning_order_id, setReassigningOrderId] = useState<string | null>(null);
  const [cashier_notes_draft, setCashierNotesDraft] = useState('');
  const [weight_kg_draft, setWeightKgDraft] = useState('');
  const [weight_locked, setWeightLocked] = useState(false);
  const [consulting_room_id_draft, setConsultingRoomIdDraft] = useState('');
  const weight_input_ref = useRef<HTMLInputElement | null>(null);
  const user_roles = useAuthStore((s) => s.user?.roles ?? []);
  const user_perms = useAuthStore((s) => s.user?.permissions ?? []);
  const cashier_name = useAuthStore((s) => s.user?.full_name ?? 'Caja');
  const is_admin = user_roles.includes('administrator');

  const setActiveShift = useActiveShiftStore((s) => s.setActive);
  const clearActiveShift = useActiveShiftStore((s) => s.clear);
  const active_shift_id = useActiveShiftStore((s) => s.shift_id);
  const active_shift_branch_id = useActiveShiftStore((s) => s.branch_id);
  const { data: shift_data } = useQuery<MyActiveShiftResponse>(MY_ACTIVE_CASH_SHIFT_QUERY, {
    fetchPolicy: 'cache-and-network',
    pollInterval: 60_000,
  });
  const { data: shift_status_data } = useQuery<MyCashShiftStatusResponse>(
    MY_CASH_SHIFT_STATUS_QUERY,
    {
      fetchPolicy: 'cache-and-network',
      pollInterval: 60_000,
    },
  );
  useEffect(() => {
    const s = shift_data?.myActiveCashShift;
    if (s) {
      setActiveShift({
        shift_id: s.id,
        cash_register_id: s.cash_register_id,
        branch_id: s.branch_id,
        opened_at: s.opened_at,
      });
    } else {
      clearActiveShift();
    }
  }, [shift_data, setActiveShift, clearActiveShift]);
  const has_open_shift = active_shift_id !== null;
  const has_branch_open_shift = shift_status_data?.myCashShiftStatus.has_branch_open_shift ?? false;
  const branch_open_shift_cashier_name =
    shift_status_data?.myCashShiftStatus.branch_open_shift_cashier_name ?? null;
  const no_shift_banner_text =
    !has_open_shift && has_branch_open_shift
      ? t('cashier_pos.no_open_shift_assigned_to_other_cashier_banner', {
          cashier: branch_open_shift_cashier_name ?? t('cashier_pos.other_cashier_fallback'),
        })
      : t('cashier_pos.no_open_shift_banner');

  const { data: pending_data, refetch } = useQuery<PendingResponse>(PENDING_SALE_ORDERS_QUERY, {
    variables: {
      offset: 0,
      limit: 25,
      customer_id: filter_customer_id || null,
      pet_id: null,
      folio_number: filter_folio || null,
      veterinarian_id: filter_vet_id || null,
      include_sent_to_doctor_pending_take: true,
    },
    fetchPolicy: 'cache-and-network',
    pollInterval: 30_000,
  });

  const { data: selected_data, refetch: refetchSelected } = useQuery<CashierOrderResponse>(
    CASHIER_SALE_ORDER_QUERY,
    {
      variables: { id: selected_id ?? '' },
      skip: !selected_id,
      fetchPolicy: 'cache-and-network',
    },
  );

  const { data: history_data, refetch: refetchHistory } = useQuery<CashierShiftSaleHistoryResponse>(
    CASHIER_SHIFT_SALE_HISTORY_QUERY,
    {
      variables: { cash_shift_id: active_shift_id ?? '', offset: 0, limit: 25 },
      skip: !active_shift_id,
      fetchPolicy: 'cache-and-network',
      pollInterval: 30_000,
    },
  );

  const { data: open_accounts_data, refetch: refetchOpenAccounts } = useQuery<OpenAccountsResponse>(
    OPEN_ACCOUNTS_QUERY,
    {
      variables: {
        offset: 0,
        limit: 24,
        kinds: [OPEN_ACCOUNT_KIND_TO_GQL.hospitalization_1, OPEN_ACCOUNT_KIND_TO_GQL.hospitalization_2],
        status: OPEN_ACCOUNT_STATUS_TO_GQL.open,
      },
      fetchPolicy: 'cache-and-network',
      pollInterval: 60_000,
    },
  );

  // Citas panel — checked-in appointments (each holds the auto-created created_by_cashier order).
  const { data: citas_data, refetch: refetchCitas } = useQuery(APPOINTMENTS_CHECKED_IN_QUERY, {
    variables: { branch_id: null },
    fetchPolicy: 'cache-and-network',
    pollInterval: 30_000,
  });
  const { data: appt_vets_data } = useQuery(VETERINARIAN_USERS_QUERY, { fetchPolicy: 'cache-first' });
  const citas_arrivals = (
    (citas_data as { appointmentsCheckedIn?: AppointmentArrival[] } | undefined)?.appointmentsCheckedIn ?? []
  ).filter((a) => a.sale_order_id);
  const appt_vet_name = (id: string): string =>
    (
      appt_vets_data as { veterinarianUsers?: { id: string; full_name: string }[] } | undefined
    )?.veterinarianUsers?.find((v) => v.id === id)?.full_name ?? '—';

  const { data: rooms_data } = useQuery<ConsultingRoomsResponse>(CASHIER_CONSULTING_ROOMS_QUERY, {
    fetchPolicy: 'cache-and-network',
  });

  const [createCashierMedicalOrder, { loading: creating_order }] = useMutation<{
    createCashierMedicalOrder: SaleOrder;
  }>(CREATE_CASHIER_MEDICAL_ORDER_MUTATION);
  const [updateCashierMedicalOrder, { loading: updating_order_meta }] = useMutation<{
    updateCashierMedicalOrder: SaleOrder;
  }>(UPDATE_CASHIER_MEDICAL_ORDER_MUTATION);
  const [sendCashierMedicalOrderToDoctor, { loading: sending_to_doctor }] = useMutation<{
    sendCashierMedicalOrderToDoctor: SaleOrder;
  }>(SEND_CASHIER_MEDICAL_ORDER_TO_DOCTOR_MUTATION);
  const [discardCashierOpeningOrder, { loading: discarding_opening }] = useMutation<{
    discardCashierOpeningOrder: SaleOrder;
  }>(DISCARD_CASHIER_OPENING_ORDER_MUTATION);
  const [reassignCashierPendingConsultingRoom] =
    useMutation<ReassignCashierPendingConsultingRoomResponse>(
      REASSIGN_CASHIER_PENDING_CONSULTING_ROOM_MUTATION,
    );
  const [addCashierSaleLine, { loading: adding_line }] = useMutation<{
    addCashierSaleLine: SaleOrder;
  }>(ADD_CASHIER_SALE_LINE_MUTATION);
  const [updateCashierSaleLine, { loading: updating_line }] = useMutation<{
    updateCashierSaleLine: SaleOrder;
  }>(UPDATE_CASHIER_SALE_LINE_MUTATION);
  const [removeCashierSaleLine, { loading: removing_line }] = useMutation<{
    removeCashierSaleLine: SaleOrder;
  }>(REMOVE_CASHIER_SALE_LINE_MUTATION);
  const [applyCashierOrderDiscount, { loading: applying_discount }] = useMutation<{
    applyCashierOrderDiscount: SaleOrder;
  }>(APPLY_CASHIER_ORDER_DISCOUNT_MUTATION);
  const [chargeSaleOrder, { loading: charging }] = useMutation<{ chargeSaleOrder: SaleOrder }>(
    CHARGE_SALE_ORDER_MUTATION,
  );
  const [returnToDoctor, { loading: returning }] = useMutation(
    RETURN_SALE_ORDER_TO_DOCTOR_MUTATION,
  );

  const orders = (pending_data?.pendingSaleOrders.items ?? [])
    .map(normalizeSaleOrder)
    .filter((order) => !charged_order_ids.has(order.id));
  const history_entries = (history_data?.cashierShiftSaleHistory ?? []).map((entry) => ({
    ...entry,
    sale_order: normalizeSaleOrder(entry.sale_order),
  }));
  const open_accounts = (open_accounts_data?.openAccounts.items ?? []).map((account) => ({
    ...account,
    kind:
      OPEN_ACCOUNT_KIND_FROM_GQL[account.kind as keyof typeof OPEN_ACCOUNT_KIND_FROM_GQL] ??
      account.kind,
  }));
  const hospitalization_open_accounts = open_accounts.filter(
    (account) => account.kind === 'hospitalization_1' || account.kind === 'hospitalization_2',
  );
  const selected = selected_data?.cashierSaleOrder
    ? normalizeSaleOrder(selected_data.cashierSaleOrder)
    : null;
  const selected_is_opening =
    selected?.status === 'created_by_cashier' ||
    selected?.status === 'returned_to_cashier_for_correction';
  const selected_is_chargeable = selected?.status === 'sent_to_cashier_for_charge';
  const rooms_for_selected_branch = (rooms_data?.consultingRooms.items ?? []).filter(
    (room) => room.status === 'active' && (!selected || room.branch_id === selected.branch_id),
  );
  const active_rooms_by_branch = useMemo(() => {
    const map = new Map<string, ConsultingRoomLite[]>();
    for (const room of rooms_data?.consultingRooms.items ?? []) {
      if (room.status !== 'active') continue;
      const current = map.get(room.branch_id) ?? [];
      current.push(room);
      map.set(room.branch_id, current);
    }
    return map;
  }, [rooms_data?.consultingRooms.items]);
  const grouped_selected_items = useMemo(
    () => groupItemsByKit(selected?.items ?? []),
    [selected?.items],
  );
  const opening_selected_items = useMemo(
    () => [...(selected?.items ?? [])].sort((a, b) => b.line_no - a.line_no),
    [selected?.items],
  );

  useEffect(() => {
    if (!selected || !selected_is_opening) return;
    setCashierNotesDraft(selected.cashier_initial_notes ?? '');
    setWeightKgDraft(
      selected.current_weight_grams ? (selected.current_weight_grams / 1000).toFixed(3) : '',
    );
    setWeightLocked(Boolean(selected.current_weight_grams));
    setConsultingRoomIdDraft(selected.consulting_room_id ?? '');
  }, [
    selected?.id,
    selected?.cashier_initial_notes,
    selected?.current_weight_grams,
    selected?.consulting_room_id,
    selected_is_opening,
  ]);

  const loadMissingKitBreakdowns = useCallback(
    async (items: readonly SaleOrderItem[]): Promise<Record<string, string[]>> => {
      const targets = collectKitBreakdownTargets(items);
      const missing_targets = targets.filter(
        (target) => !Object.prototype.hasOwnProperty.call(kit_breakdowns, target.key),
      );
      if (missing_targets.length === 0) return kit_breakdowns;

      const fetched: Record<string, string[]> = {};
      for (const target of missing_targets) {
        try {
          if (target.kind === 'id') {
            const res = await apollo.query<{ kitBreakdownLines: string[] }>({
              query: KIT_BREAKDOWN_LINES_QUERY,
              variables: { kit_id: target.value },
              fetchPolicy: 'network-only',
            });
            fetched[target.key] = res.data?.kitBreakdownLines ?? [];
            continue;
          }
          const res = await apollo.query<{ kitBreakdownLinesByCode: string[] }>({
            query: KIT_BREAKDOWN_LINES_BY_CODE_QUERY,
            variables: { kit_code: target.value },
            fetchPolicy: 'network-only',
          });
          fetched[target.key] = res.data?.kitBreakdownLinesByCode ?? [];
        } catch {
          fetched[target.key] = [];
        }
      }

      const merged = { ...kit_breakdowns, ...fetched };
      setKitBreakdowns(merged);
      return merged;
    },
    [apollo, kit_breakdowns],
  );

  // F5 manual refresh.
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'F5') {
        event.preventDefault();
        void refetch();
        if (active_shift_id) void refetchHistory();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [refetch, refetchHistory, active_shift_id]);

  useEffect(() => {
    if (!selected) return;
    void loadMissingKitBreakdowns(selected.items);
  }, [selected?.id, selected?.items, loadMissingKitBreakdowns]);

  useEffect(() => {
    if (!history_detail) return;
    void loadMissingKitBreakdowns(history_detail.sale_order.items);
  }, [history_detail?.sale_order_id, history_detail?.sale_order.items, loadMissingKitBreakdowns]);

  const onClickSelectOrder = useCallback((id: string): void => {
    setSelectedId(id);
    setLastPaidFolio(null);
    setErrorText(null);
  }, []);

  // Deep-link from the Citas board ("Ir a caja"): ?citas=1&order=<sale_order_id> expands the
  // Citas panel and pre-selects the order so the cashier lands straight on it.
  useEffect(() => {
    const deep_link_order = search_params.get('order');
    if (search_params.get('citas') === '1') setIsCitasCollapsed(false);
    if (deep_link_order) onClickSelectOrder(deep_link_order);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onClickClearSelection = useCallback((): void => {
    setSelectedId(null);
    setErrorText(null);
  }, []);

  const onCreateOpeningOrder = useCallback(async (): Promise<void> => {
    setErrorText(null);
    try {
      const result = await createCashierMedicalOrder({
        variables: { input: {} },
        context: { headers: { 'Idempotency-Key': crypto.randomUUID() } },
      });
      const created_id = result.data?.createCashierMedicalOrder.id;
      if (created_id) setSelectedId(created_id);
      await refetch();
    } catch (err) {
      applyApolloErrorMessage(err, t, setErrorText);
    }
  }, [createCashierMedicalOrder, refetch, t]);

  const syncOpeningMeta = useCallback(
    async (options?: { force?: boolean }): Promise<void> => {
      if (!selected || !selected_is_opening) return;
      const weight_grams = parseWeightKgToGrams(weight_kg_draft);
      const next_room_id = consulting_room_id_draft || null;
      const next_notes = normalizeOptionalText(cashier_notes_draft);
      const current_notes = normalizeOptionalText(selected.cashier_initial_notes ?? null);
      const needs_sync =
        selected.consulting_room_id !== next_room_id ||
        current_notes !== next_notes ||
        (selected.current_weight_grams ?? null) !== (weight_grams ?? null);
      if (!options?.force && !needs_sync) return;

      await updateCashierMedicalOrder({
        variables: {
          input: {
            sale_order_id: selected.id,
            consulting_room_id: next_room_id,
            cashier_initial_notes: cashier_notes_draft,
            current_weight_grams: weight_grams,
          },
        },
        context: { headers: { 'Idempotency-Key': crypto.randomUUID() } },
      });
    },
    [
      selected,
      selected_is_opening,
      weight_kg_draft,
      consulting_room_id_draft,
      cashier_notes_draft,
      updateCashierMedicalOrder,
    ],
  );

  const onSaveOpeningMeta = useCallback(async (): Promise<void> => {
    if (!selected || !selected_is_opening) return;
    setErrorText(null);
    try {
      await syncOpeningMeta({ force: true });
      await refetch();
    } catch (err) {
      applyApolloErrorMessage(err, t, setErrorText);
    }
  }, [selected, selected_is_opening, syncOpeningMeta, refetch, t]);

  const onWeightBlurSave = useCallback(async (): Promise<void> => {
    if (!selected || !selected_is_opening || weight_locked) return;
    if (!weight_kg_draft.trim()) return;
    setErrorText(null);
    try {
      await syncOpeningMeta({ force: true });
      await refetch();
      setWeightLocked(true);
    } catch (err) {
      applyApolloErrorMessage(err, t, setErrorText);
    }
  }, [selected, selected_is_opening, weight_locked, weight_kg_draft, syncOpeningMeta, refetch, t]);

  const onUnlockWeight = useCallback((): void => {
    setWeightLocked(false);
    window.setTimeout(() => {
      weight_input_ref.current?.focus();
      weight_input_ref.current?.select();
    }, 0);
  }, []);

  const onChangeOpeningCustomerPet = useCallback(
    async (next: { customer_id: string | null; pet_id: string | null }): Promise<void> => {
      if (!selected || !selected_is_opening) return;
      setErrorText(null);
      try {
        await updateCashierMedicalOrder({
          variables: {
            input: {
              sale_order_id: selected.id,
              customer_id: next.customer_id,
              pet_id: next.pet_id,
            },
          },
          context: { headers: { 'Idempotency-Key': crypto.randomUUID() } },
        });
        await refetch();
      } catch (err) {
        applyApolloErrorMessage(err, t, setErrorText);
      }
    },
    [selected, selected_is_opening, updateCashierMedicalOrder, refetch, t],
  );

  const onAddOpeningLine = useCallback(
    async (hit: SellableHit, quantity: string): Promise<void> => {
      if (!selected || !selected_is_opening) return;
      setErrorText(null);
      try {
        await syncOpeningMeta();
        await addCashierSaleLine({
          variables: {
            input: {
              sale_order_id: selected.id,
              item_kind: hit.kind.toUpperCase(),
              product_id: hit.kind === 'product' ? hit.id : null,
              service_id: hit.kind === 'service' ? hit.id : null,
              kit_id: hit.kind === 'kit' ? hit.id : null,
              quantity,
            },
          },
        });
        await refetch();
      } catch (err) {
        applyApolloErrorMessage(err, t, setErrorText);
      }
    },
    [selected, selected_is_opening, syncOpeningMeta, addCashierSaleLine, refetch, t],
  );

  const onUpdateOpeningLineQty = useCallback(
    async (item_id: string, quantity: string): Promise<void> => {
      setErrorText(null);
      try {
        await updateCashierSaleLine({
          variables: { input: { sale_order_item_id: item_id, quantity } },
        });
        await refetch();
      } catch (err) {
        applyApolloErrorMessage(err, t, setErrorText);
      }
    },
    [updateCashierSaleLine, refetch, t],
  );

  const onRemoveOpeningLine = useCallback(
    async (item_id: string): Promise<void> => {
      setErrorText(null);
      try {
        await removeCashierSaleLine({
          variables: { sale_order_item_id: item_id },
        });
        await refetch();
      } catch (err) {
        applyApolloErrorMessage(err, t, setErrorText);
      }
    },
    [removeCashierSaleLine, refetch, t],
  );

  const onSendToDoctor = useCallback(async (): Promise<void> => {
    if (!selected || !selected_is_opening) return;
    setErrorText(null);
    const effective_consulting_room_id = consulting_room_id_draft || selected.consulting_room_id;
    const effective_weight_grams = parseWeightKgToGrams(weight_kg_draft);
    if (!selected.customer_id) {
      setErrorText(t('doctor_pos.customer_required'));
      return;
    }
    if (!selected.pet_id) {
      setErrorText(t('doctor_pos.pet_required'));
      return;
    }
    if (!effective_weight_grams || effective_weight_grams <= 0) {
      setErrorText(t('doctor_pos.weight_missing'));
      return;
    }
    if (!effective_consulting_room_id) {
      setErrorText(t('cashier_pos.select_consulting_room'));
      return;
    }
    if (selected.items.length === 0) {
      setErrorText(t('doctor_pos.lines_required'));
      return;
    }
    try {
      await syncOpeningMeta({ force: true });
      await sendCashierMedicalOrderToDoctor({
        variables: { input: { sale_order_id: selected.id } },
        context: { headers: { 'Idempotency-Key': crypto.randomUUID() } },
      });
      await refetch();
      setSelectedId(null);
    } catch (err) {
      applyApolloErrorMessage(err, t, setErrorText);
    }
  }, [
    selected,
    selected_is_opening,
    syncOpeningMeta,
    sendCashierMedicalOrderToDoctor,
    consulting_room_id_draft,
    weight_kg_draft,
    refetch,
    t,
  ]);

  const onDiscardOpeningOrder = useCallback(
    async (sale_order_id: string): Promise<void> => {
      setErrorText(null);
      try {
        await discardCashierOpeningOrder({
          variables: { input: { sale_order_id } },
          context: { headers: { 'Idempotency-Key': crypto.randomUUID() } },
        });
        if (selected_id === sale_order_id) {
          setSelectedId(null);
        }
        setShowDiscardConfirm(false);
        setDiscardTargetId(null);
        await refetch();
      } catch (err) {
        applyApolloErrorMessage(err, t, setErrorText);
      }
    },
    [discardCashierOpeningOrder, refetch, selected_id, t],
  );

  const onCharge = useCallback(
    async (
      drafts: PaymentDraftValue[],
      options: { open_account_kind: 'normal' | 'hospitalization_1' | 'hospitalization_2' | null },
    ): Promise<void> => {
      if (!selected) return;
      setErrorText(null);
      const idempotency_key =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `np-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const ticket_window =
        typeof window !== 'undefined' ? window.open('', '_blank', 'width=420,height=720') : null;
      try {
        const charge_input = {
          sale_order_id: selected.id,
          payments: drafts.map((d) => {
            const trimmed_ref = d.reference_number?.trim();
            return {
              payment_method_id: d.payment_method_id,
              amount_cents: d.amount_cents,
              reference_number: trimmed_ref && trimmed_ref.length > 0 ? trimmed_ref : null,
              allow_change: d.allow_change,
            };
          }),
          open_account_kind: options.open_account_kind
            ? OPEN_ACCOUNT_KIND_TO_GQL[options.open_account_kind]
            : null,
          auto_create_open_account: options.open_account_kind !== null,
        };
        // eslint-disable-next-line @typescript-eslint/naming-convention
        const charge_context = { headers: { 'Idempotency-Key': idempotency_key } };
        const charged: SaleOrder | undefined = (
          await chargeSaleOrder({
            variables: { input: charge_input },
            context: charge_context,
          })
        ).data?.chargeSaleOrder;
        const charged_order = charged ? normalizeSaleOrder(charged) : selected;
        setChargedOrderIds((prev) => new Set(prev).add(selected.id));
        setSelectedId(null);
        setShowReturn(false);
        setShowCancel(false);
        setErrorText(null);
        setLastPaidFolio(charged_order.folio_number ?? selected.id.slice(0, 8));
        if (selected.customer_id) {
          setLastPaidSale({ id: selected.id, customer_id: selected.customer_id });
        } else {
          setLastPaidSale(null);
        }
        await refetch();
        if (active_shift_id) await refetchHistory();
        window.setTimeout(() => {
          const submitted_total_cents = drafts.reduce<bigint>(
            (acc, draft) => acc + safeTicketBigInt(draft.amount_cents),
            0n,
          );
          const ticket_total_cents = safeTicketBigInt(charged_order.total_cents);
          const open_account_pending_cents =
            options.open_account_kind !== null && submitted_total_cents < ticket_total_cents
              ? (ticket_total_cents - submitted_total_cents).toString()
              : null;
          const open_account_credit_cents =
            options.open_account_kind === 'hospitalization_1' &&
            submitted_total_cents > ticket_total_cents
              ? (submitted_total_cents - ticket_total_cents).toString()
              : null;
          void loadMissingKitBreakdowns(charged_order.items).then((resolved_breakdowns) =>
            printSaleTicket(
              charged_order,
              {
                mode: 'ticket',
                cashier_name,
                ticket_number: ticketLabel(null, charged_order.folio_number),
                kit_breakdowns: resolved_breakdowns,
                payment_methods: drafts.map((draft) => ({
                  payment_method_id: draft.payment_method_id,
                  payment_method_code: draft.method_code,
                  payment_method_name: draft.method_name,
                  amount_cents: draft.amount_cents,
                  reference_number: draft.reference_number,
                })),
                open_account_kind: options.open_account_kind,
                open_account_pending_cents,
                open_account_credit_cents,
                payment_method_summary: summarizePaymentMethodLines(drafts),
              },
              ticket_window,
            ),
          );
        }, 0);
      } catch (err) {
        ticket_window?.close();
        applyApolloErrorMessage(err, t, setErrorText);
      }
    },
    [
      selected,
      chargeSaleOrder,
      refetch,
      refetchHistory,
      active_shift_id,
      cashier_name,
      loadMissingKitBreakdowns,
      t,
    ],
  );

  const onSubmitReturn = useCallback(
    async (input: { cancellation_reason_id: string | null; note: string }): Promise<void> => {
      if (!selected) return;
      setErrorText(null);
      try {
        await returnToDoctor({
          variables: {
            input: {
              sale_order_id: selected.id,
              cancellation_reason_id: input.cancellation_reason_id,
              note: input.note,
            },
          },
        });
        setShowReturn(false);
        setSelectedId(null);
        await refetch();
      } catch (err) {
        applyApolloErrorMessage(err, t, setErrorText);
      }
    },
    [selected, returnToDoctor, refetch, t],
  );

  const onReassignPendingConsultingRoom = useCallback(
    async (order: SaleOrder): Promise<void> => {
      const consulting_room_id =
        reassign_room_by_order_id[order.id] ?? order.consulting_room_id ?? '';
      const reason = (reassign_reason_by_order_id[order.id] ?? '').trim();
      if (!consulting_room_id) {
        setErrorText(t('cashier_pos.select_consulting_room'));
        return;
      }
      if (!reason) {
        setErrorText(t('cashier_pos.reassign_reason_required'));
        return;
      }
      setErrorText(null);
      setReassigningOrderId(order.id);
      try {
        const result = await reassignCashierPendingConsultingRoom({
          variables: {
            input: {
              sale_order_id: order.id,
              consulting_room_id,
              reason,
            },
          },
          context: { headers: { 'Idempotency-Key': crypto.randomUUID() } },
        });
        const advisory = result.data?.reassignCashierPendingConsultingRoom.advisory_code ?? null;
        setReassignAdvisoryByOrderId((prev) => ({
          ...prev,
          [order.id]:
            advisory === 'consulting_room_occupied'
              ? t('cashier_pos.reassign_advisory_room_occupied')
              : null,
        }));
        await refetch();
      } catch (err) {
        applyApolloErrorMessage(err, t, setErrorText);
      } finally {
        setReassigningOrderId(null);
      }
    },
    [
      reassign_room_by_order_id,
      reassign_reason_by_order_id,
      reassignCashierPendingConsultingRoom,
      refetch,
      t,
    ],
  );

  const charge_queue_orders = useMemo(
    () =>
      orders
        .filter((order) => order.status === 'sent_to_cashier_for_charge')
        .sort((a, b) => {
          const at = a.sent_to_cashier_at ?? a.created_at ?? '';
          const bt = b.sent_to_cashier_at ?? b.created_at ?? '';
          return at.localeCompare(bt);
        }),
    [orders],
  );
  const opening_queue_orders = useMemo(
    () =>
      orders
        .filter(
          (order) =>
            // Appointment-arrival orders move to the dedicated Citas panel; keep walk-ins
            // (created_by_cashier with no appointment) and all corrections here.
            (order.status === 'created_by_cashier' && !order.appointment_id) ||
            order.status === 'returned_to_cashier_for_correction',
        )
        .sort((a, b) => {
          const at = a.created_at ?? '';
          const bt = b.created_at ?? '';
          return at.localeCompare(bt);
        }),
    [orders],
  );
  const sent_to_doctor_queue_orders = useMemo(
    () =>
      orders
        .filter((order) => order.status === 'sent_to_doctor_pending_take')
        .sort((a, b) => {
          const at = a.created_at ?? '';
          const bt = b.created_at ?? '';
          return at.localeCompare(bt);
        }),
    [orders],
  );
  const sent_to_doctor_branch_id =
    active_shift_branch_id ?? sent_to_doctor_queue_orders[0]?.branch_id ?? null;
  const sent_to_doctor_room_sections = useMemo(() => {
    const rooms = sent_to_doctor_branch_id
      ? [...(active_rooms_by_branch.get(sent_to_doctor_branch_id) ?? [])].sort((a, b) =>
          a.display_order === b.display_order
            ? a.name.localeCompare(b.name, 'es-MX')
            : a.display_order - b.display_order,
        )
      : [];
    const room_ids = new Set(rooms.map((room) => room.id));
    const grouped = new Map<string, SaleOrder[]>();
    const unassigned: SaleOrder[] = [];
    for (const order of sent_to_doctor_queue_orders) {
      if (!order.consulting_room_id || !room_ids.has(order.consulting_room_id)) {
        unassigned.push(order);
        continue;
      }
      const current = grouped.get(order.consulting_room_id) ?? [];
      current.push(order);
      grouped.set(order.consulting_room_id, current);
    }
    const sections = rooms.map((room) => ({
      key: room.id,
      label: room.name,
      orders: grouped.get(room.id) ?? [],
    }));
    if (unassigned.length > 0) {
      sections.push({
        key: '__unassigned__',
        label: t('cashier_pos.no_consulting_room'),
        orders: unassigned,
      });
    }
    return sections;
  }, [active_rooms_by_branch, sent_to_doctor_branch_id, sent_to_doctor_queue_orders, t]);
  const opening_busy =
    creating_order ||
    updating_order_meta ||
    sending_to_doctor ||
    discarding_opening ||
    adding_line ||
    updating_line ||
    removing_line;
  const reassign_busy = reassigning_order_id !== null;
  const onRequestDiscardOpeningOrder = useCallback((sale_order_id: string): void => {
    setDiscardTargetId(sale_order_id);
    setShowDiscardConfirm(true);
  }, []);
  const renderOrdersGrid = useCallback(
    (
      list: SaleOrder[],
      options?: { allow_discard?: boolean; allow_reassign?: boolean },
    ): JSX.Element => (
      <div className="grid gap-3 md:grid-cols-2">
        {list.map((order) => (
          <Card
            key={order.id}
            role="button"
            tabIndex={0}
            onClick={() => onClickSelectOrder(order.id)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onClickSelectOrder(order.id);
              }
            }}
            className={`cursor-pointer text-left transition-colors ${
              order.id === selected_id ? 'ring-primary ring-2 ring-offset-2' : ''
            } border border-border bg-card shadow-sm hover:border-primary/50 hover:shadow-md`}
          >
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-muted-foreground text-xs font-medium uppercase tracking-wide">
                    {order.consulting_room_name ?? t('cashier_pos.no_consulting_room')}
                  </p>
                  <CardTitle className="mt-1 truncate text-base">
                    {customerLabel(order.customer_snapshot_json)}
                  </CardTitle>
                </div>
                <div className="flex items-center gap-2">
                  {order.appointment_id ? <Badge variant="outline">{t('cashier_pos.from_appointment')}</Badge> : null}
                  <Badge variant="muted">{petLabel(order.pet_snapshot_json)}</Badge>
                  {options?.allow_discard ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={(event) => {
                        event.stopPropagation();
                        onRequestDiscardOpeningOrder(order.id);
                      }}
                      disabled={discarding_opening || opening_busy}
                      aria-label={t('cashier_pos.discard_opening')}
                      title={t('cashier_pos.discard_opening')}
                    >
                      <Trash2 className="text-destructive h-4 w-4" aria-hidden />
                    </Button>
                  ) : null}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="text-muted-foreground grid gap-1">
                <span>
                  {t('cashier_pos.patient')}: {customerLabel(order.customer_snapshot_json)}
                </span>
                <span>
                  {t('cashier_pos.pet')}: {petLabel(order.pet_snapshot_json)}
                </span>
              </div>
              <div className="text-muted-foreground flex items-center justify-between">
                <span>
                  {order.items.length} {t('cashier_pos.items')}
                </span>
                <span className="font-mono text-xs">
                  {dateTimeLabel(order.sent_to_cashier_at ?? order.created_at)}
                </span>
              </div>
              <div>
                <span
                  className={cn(
                    'inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide',
                    order.status === 'sent_to_cashier_for_charge'
                      ? 'bg-success/15 text-success'
                      : 'bg-primary/10 text-primary',
                  )}
                >
                  {t(`doctor_pos.status_${order.status}`)}
                </span>
              </div>
              <div className="flex items-center justify-between font-semibold">
                <span>{t('cashier_pos.total')}</span>
                <span className="font-mono">{formatCentsString(order.total_cents)}</span>
              </div>
              {options?.allow_reassign ? (
                <div className="space-y-2 rounded-md border border-border bg-secondary p-2">
                  <div className="space-y-1">
                    <Label className="text-muted-foreground text-[11px] uppercase tracking-wide">
                      {t('cashier_pos.consulting_room')}
                    </Label>
                    <select
                      className="border-input bg-background h-9 w-full rounded-md border px-2 text-xs"
                      value={reassign_room_by_order_id[order.id] ?? order.consulting_room_id ?? ''}
                      onChange={(event) =>
                        setReassignRoomByOrderId((prev) => ({
                          ...prev,
                          [order.id]: event.target.value,
                        }))
                      }
                      disabled={reassign_busy}
                      onClick={(event) => event.stopPropagation()}
                    >
                      <option value="">{t('cashier_pos.select_consulting_room')}</option>
                      {(active_rooms_by_branch.get(order.branch_id) ?? []).map((room) => (
                        <option key={room.id} value={room.id}>
                          {room.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-muted-foreground text-[11px] uppercase tracking-wide">
                      {t('cashier_pos.reassign_reason')}
                    </Label>
                    <Input
                      value={reassign_reason_by_order_id[order.id] ?? ''}
                      onChange={(event) =>
                        setReassignReasonByOrderId((prev) => ({
                          ...prev,
                          [order.id]: event.target.value,
                        }))
                      }
                      placeholder={t('cashier_pos.reassign_reason_placeholder')}
                      disabled={reassign_busy}
                      onClick={(event) => event.stopPropagation()}
                    />
                  </div>
                  {reassign_advisory_by_order_id[order.id] ? (
                    <p className="text-xs text-warning">
                      {reassign_advisory_by_order_id[order.id]}
                    </p>
                  ) : null}
                  <Button
                    type="button"
                    size="sm"
                    className="w-full"
                    disabled={reassign_busy}
                    onClick={(event) => {
                      event.stopPropagation();
                      void onReassignPendingConsultingRoom(order);
                    }}
                  >
                    {t('cashier_pos.reassign_consulting_room')}
                  </Button>
                </div>
              ) : null}
            </CardContent>
          </Card>
        ))}
      </div>
    ),
    [
      active_rooms_by_branch,
      discarding_opening,
      onClickSelectOrder,
      onReassignPendingConsultingRoom,
      onRequestDiscardOpeningOrder,
      opening_busy,
      reassign_advisory_by_order_id,
      reassign_busy,
      reassign_reason_by_order_id,
      reassign_room_by_order_id,
      selected_id,
      t,
    ],
  );
  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-gradient-to-r from-card via-secondary to-primary/10 px-4 py-3 shadow-sm">
        <div>
          <h2 className="font-display text-2xl font-extrabold tracking-tight text-foreground">
            {t('nav.cashier_pos')}
          </h2>
          <p className="text-sm text-muted-foreground">{t('cashier_pos.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="sm"
            onClick={() => {
              void onCreateOpeningOrder();
            }}
            disabled={opening_busy || charging || returning}
          >
            {t('cashier_pos.new_medical_order')}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-full"
            onClick={() => setShowFilters((v) => !v)}
          >
            <Filter className="h-4 w-4" aria-hidden />
            <span>{t('cashier_pos.filters_toggle')}</span>
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="rounded-full"
            onClick={() => {
              void refetch();
              if (active_shift_id) void refetchHistory();
              void refetchOpenAccounts();
            }}
            aria-label={t('cashier_pos.refresh')}
          >
            <RefreshCw className="h-4 w-4" aria-hidden />
          </Button>
        </div>
      </header>

      {last_paid_folio ? (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-success/40 bg-success/15 p-3 text-sm">
          <CheckCircle2 className="h-4 w-4 text-success" aria-hidden />
          <span>{t('cashier_pos.charged_banner', { folio: last_paid_folio })}</span>
          {last_paid_sale && user_perms.includes('invoicing.cfdi.request') ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="ml-auto"
              onClick={() => setShowRequestCfdi(true)}
            >
              <Receipt className="h-4 w-4" aria-hidden />
              <span>{t('invoicing.cta_request')}</span>
            </Button>
          ) : null}
        </div>
      ) : null}

      {error_text ? (
        <div className="border-destructive/40 bg-destructive/5 text-destructive rounded-md border p-3 text-sm">
          {error_text}
        </div>
      ) : null}

      {!has_open_shift ? (
        <div className="flex items-center gap-2 rounded-md border border-warning/40 bg-warning/15 p-3 text-sm">
          <AlertTriangle className="h-4 w-4 text-warning" aria-hidden />
          <span>{no_shift_banner_text}</span>
          <Link
            to="/app/cash"
            className="ml-auto text-sm font-medium text-warning underline"
          >
            {t('cashier_pos.go_to_cash')}
          </Link>
        </div>
      ) : null}

      {show_filters ? (
        <Card>
          <CardContent className="grid gap-3 pt-4 md:grid-cols-3">
            <div className="space-y-1">
              <label className="text-muted-foreground text-xs font-medium uppercase tracking-wide">
                {t('cashier_pos.filter_folio')}
              </label>
              <Input
                value={filter_folio}
                onChange={(e) => setFilterFolio(e.target.value)}
                placeholder="123..."
              />
            </div>
            <div className="space-y-1">
              <label className="text-muted-foreground text-xs font-medium uppercase tracking-wide">
                {t('cashier_pos.filter_customer_id')}
              </label>
              <Input
                value={filter_customer_id}
                onChange={(e) => setFilterCustomerId(e.target.value)}
                placeholder="UUID"
              />
            </div>
            <div className="space-y-1">
              <label className="text-muted-foreground text-xs font-medium uppercase tracking-wide">
                {t('cashier_pos.filter_vet_id')}
              </label>
              <Input
                value={filter_vet_id}
                onChange={(e) => setFilterVetId(e.target.value)}
                placeholder="UUID"
              />
            </div>
          </CardContent>
        </Card>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-[2fr_1fr]">
        <div className="space-y-3">
          <section className="space-y-2 rounded-lg border border-accent/30 bg-accent/5 p-2.5 shadow-sm">
            <div className="flex items-center justify-between gap-2">
              <h3 className="font-display text-sm font-bold uppercase tracking-wide text-accent">
                {t('cashier_pos.citas_title')} — {citas_arrivals.length} {t('cashier_pos.citas_waiting')}
              </h3>
              <div className="flex items-center gap-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="rounded-full text-accent"
                  onClick={() => setIsCitasCollapsed((prev) => !prev)}
                  aria-label={
                    is_citas_collapsed ? t('cashier_pos.citas_expand') : t('cashier_pos.citas_collapse')
                  }
                  title={
                    is_citas_collapsed ? t('cashier_pos.citas_expand') : t('cashier_pos.citas_collapse')
                  }
                >
                  {is_citas_collapsed ? (
                    <ChevronDown className="h-4 w-4" aria-hidden />
                  ) : (
                    <ChevronUp className="h-4 w-4" aria-hidden />
                  )}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="rounded-full text-accent"
                  onClick={() => {
                    void refetchCitas();
                  }}
                  aria-label={t('cashier_pos.refresh')}
                >
                  <RefreshCw className="h-4 w-4" aria-hidden />
                </Button>
              </div>
            </div>
            {is_citas_collapsed ? null : citas_arrivals.length === 0 ? (
              <Card>
                <CardContent className="text-muted-foreground py-3 text-center text-sm">
                  {t('cashier_pos.citas_empty')}
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
                {citas_arrivals.map((a) => (
                  <Card key={a.id} className="border-accent/30 bg-card">
                    <CardContent className="space-y-1.5 p-2.5">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-mono text-sm font-semibold">
                          {new Date(a.scheduled_start_at).toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                        <Badge variant="outline">{a.folio_number}</Badge>
                      </div>
                      <p className="truncate text-sm font-medium">{a.pet_name ?? '—'}</p>
                      <p className="text-muted-foreground truncate text-xs">
                        {appt_vet_name(a.veterinarian_id)}
                      </p>
                      <Button
                        size="sm"
                        className="w-full"
                        disabled={!a.sale_order_id}
                        onClick={() => {
                          if (a.sale_order_id) onClickSelectOrder(a.sale_order_id);
                        }}
                      >
                        {t('cashier_pos.citas_take')}
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </section>

          <section className="space-y-3 rounded-lg border border-border bg-card p-3 shadow-sm">
            <h3 className="font-display text-sm font-bold uppercase tracking-wide text-muted-foreground">
              {t('cashier_pos.pending_charge_orders_title')} · {charge_queue_orders.length}
            </h3>
            {charge_queue_orders.length === 0 ? (
              <Card>
                <CardContent className="text-muted-foreground py-6 text-center text-sm">
                  {t('cashier_pos.pending_charge_orders_empty')}
                </CardContent>
              </Card>
            ) : (
              renderOrdersGrid(charge_queue_orders)
            )}
          </section>

          <section className="space-y-2 rounded-lg border border-warning/30 bg-warning/5 p-2.5 shadow-sm">
            <div className="flex items-center justify-between gap-2">
              <h3 className="font-display text-sm font-bold uppercase tracking-wide text-warning">
                {t('cashier_pos.hospitalizations_title')} · {hospitalization_open_accounts.length}
              </h3>
              <div className="flex items-center gap-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="rounded-full text-warning"
                  onClick={() => setIsHospitalizationsCollapsed((prev) => !prev)}
                  aria-label={
                    is_hospitalizations_collapsed
                      ? t('cashier_pos.hospitalizations_expand')
                      : t('cashier_pos.hospitalizations_collapse')
                  }
                  title={
                    is_hospitalizations_collapsed
                      ? t('cashier_pos.hospitalizations_expand')
                      : t('cashier_pos.hospitalizations_collapse')
                  }
                >
                  {is_hospitalizations_collapsed ? (
                    <ChevronDown className="h-4 w-4" aria-hidden />
                  ) : (
                    <ChevronUp className="h-4 w-4" aria-hidden />
                  )}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="rounded-full text-warning"
                  onClick={() => {
                    void refetchOpenAccounts();
                  }}
                  aria-label={t('cashier_pos.refresh')}
                >
                  <RefreshCw className="h-4 w-4" aria-hidden />
                </Button>
              </div>
            </div>
            {is_hospitalizations_collapsed ? null : hospitalization_open_accounts.length === 0 ? (
              <Card>
                <CardContent className="text-muted-foreground py-3 text-center text-sm">
                  {t('open_accounts.empty')}
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
                {hospitalization_open_accounts.map((account) => (
                  <button
                    key={account.id}
                    type="button"
                    className="text-left"
                    onClick={() => setOpenAccountDetailId(account.id)}
                  >
                    <Card className="border-warning/30 bg-card transition hover:border-warning/60 hover:shadow-sm">
                      <CardContent className="space-y-2 p-2.5">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 space-y-0.5">
                            <span className="inline-flex items-center gap-2 text-sm font-semibold">
                              <Wallet className="h-4 w-4 text-warning" aria-hidden />
                              <span className="font-mono">
                                #{account.folio_number ?? account.id.slice(0, 8)}
                              </span>
                            </span>
                            <p className="truncate text-sm leading-tight">
                              {t('open_accounts.tutor')}:{' '}
                              <span className="text-foreground font-medium">
                                {account.tutor_name ?? account.customer_id}
                              </span>
                            </p>
                            <p className="text-muted-foreground truncate text-xs leading-tight">
                              {t('open_accounts.pet')}:{' '}
                              <span className="text-foreground">
                                {account.pet_name ?? account.pet_id}
                              </span>
                            </p>
                          </div>
                          <div className="flex flex-wrap justify-end gap-1.5">
                            <Badge variant="outline" className="text-[11px]">
                              {t(`open_accounts.kind_${account.kind}`)}
                            </Badge>
                            <Badge variant="success" className="text-[11px]">
                              {t('open_accounts.status_open')}
                            </Badge>
                          </div>
                        </div>
                        <div className="text-muted-foreground grid gap-0.5 text-xs">
                          <p>
                            {t('open_accounts.opened_at')}: {dateTimeLabel(account.opened_at)}
                          </p>
                        </div>
                        <div className="flex items-center justify-between border-t border-warning/20 pt-1.5 text-sm">
                          <span className="text-muted-foreground">
                            {t('open_accounts.balance')}
                          </span>
                          <span className="font-mono font-semibold tabular-nums text-warning">
                            {formatCentsString(account.balance_cents)}
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  </button>
                ))}
              </div>
            )}
          </section>

          <section className="space-y-3 rounded-lg border border-border bg-secondary p-3 shadow-sm">
            <div className="flex items-center justify-between gap-2">
              <h3 className="font-display text-sm font-bold uppercase tracking-wide text-muted-foreground">
                {t('cashier_pos.sent_to_doctor_orders_title')} ·{' '}
                {sent_to_doctor_queue_orders.length}
              </h3>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="rounded-full"
                onClick={() => setIsSentToDoctorCollapsed((prev) => !prev)}
                aria-label={
                  is_sent_to_doctor_collapsed
                    ? t('cashier_pos.sent_to_doctor_expand')
                    : t('cashier_pos.sent_to_doctor_collapse')
                }
                title={
                  is_sent_to_doctor_collapsed
                    ? t('cashier_pos.sent_to_doctor_expand')
                    : t('cashier_pos.sent_to_doctor_collapse')
                }
              >
                {is_sent_to_doctor_collapsed ? (
                  <ChevronDown className="h-4 w-4" aria-hidden />
                ) : (
                  <ChevronUp className="h-4 w-4" aria-hidden />
                )}
              </Button>
            </div>
            {is_sent_to_doctor_collapsed
              ? null
              : sent_to_doctor_room_sections.map((section) => (
                  <div
                    key={section.key}
                    className="space-y-2 rounded-md border border-border bg-card p-2"
                  >
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      {section.label} · {section.orders.length}
                    </h4>
                    {section.orders.length === 0 ? (
                      <Card>
                        <CardContent className="text-muted-foreground py-3 text-center text-xs">
                          {t('cashier_pos.sent_to_doctor_room_empty')}
                        </CardContent>
                      </Card>
                    ) : (
                      renderOrdersGrid(section.orders, { allow_reassign: true })
                    )}
                  </div>
                ))}
          </section>

          <section className="space-y-3 rounded-lg border border-border bg-secondary p-3 shadow-sm">
            <div className="pt-2">
              <div className="flex items-center justify-between gap-2">
                <h3 className="font-display text-sm font-bold uppercase tracking-wide text-muted-foreground">
                  {t('cashier_pos.opening_orders_title')} · {opening_queue_orders.length}
                </h3>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="rounded-full"
                  onClick={() => setIsOpeningCollapsed((prev) => !prev)}
                  aria-label={
                    is_opening_collapsed
                      ? t('cashier_pos.opening_expand')
                      : t('cashier_pos.opening_collapse')
                  }
                  title={
                    is_opening_collapsed
                      ? t('cashier_pos.opening_expand')
                      : t('cashier_pos.opening_collapse')
                  }
                >
                  {is_opening_collapsed ? (
                    <ChevronDown className="h-4 w-4" aria-hidden />
                  ) : (
                    <ChevronUp className="h-4 w-4" aria-hidden />
                  )}
                </Button>
              </div>
              <p className="text-muted-foreground mt-1 text-xs">
                {t('cashier_pos.opening_orders_hint')}
              </p>
            </div>
            {is_opening_collapsed ? null : opening_queue_orders.length === 0 ? (
              <Card>
                <CardContent className="text-muted-foreground py-4 text-center text-sm">
                  {t('cashier_pos.opening_orders_empty')}
                </CardContent>
              </Card>
            ) : (
              renderOrdersGrid(opening_queue_orders, { allow_discard: true })
            )}
          </section>
        </div>
        <div className="space-y-3 lg:sticky lg:top-4 lg:self-start">
          {selected ? (
            selected_is_opening ? (
              <Card className="border-border bg-card shadow-sm">
                <CardHeader className="flex flex-row items-start justify-between gap-2">
                  <div>
                    <p className="text-muted-foreground text-xs font-medium uppercase tracking-wide">
                      {selected.consulting_room_name ?? t('cashier_pos.no_consulting_room')}
                    </p>
                    <CardTitle className="text-base">
                      {t('cashier_pos.opening_mode_title')}
                    </CardTitle>
                    <p className="text-muted-foreground text-xs">
                      {t(`doctor_pos.status_${selected.status}`)}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        onRequestDiscardOpeningOrder(selected.id);
                      }}
                      disabled={opening_busy}
                    >
                      <Trash2 className="text-destructive h-4 w-4" aria-hidden />
                      <span>{t('cashier_pos.discard_opening')}</span>
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        void onSaveOpeningMeta();
                      }}
                      disabled={opening_busy}
                    >
                      {t('common.save')}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <CustomerPetPicker
                    customer_id={selected.customer_id}
                    pet_id={selected.pet_id}
                    onChange={(next) => {
                      void onChangeOpeningCustomerPet(next);
                    }}
                    disabled={opening_busy}
                  />
                  <div className="grid gap-3 md:grid-cols-2">
                    <div className="space-y-1">
                      <Label className="text-xs">{t('cashier_pos.current_weight_kg')}</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          ref={weight_input_ref}
                          value={weight_kg_draft}
                          onChange={(e) => setWeightKgDraft(e.target.value)}
                          onBlur={() => {
                            void onWeightBlurSave();
                          }}
                          placeholder="Ej. 4.250"
                          disabled={opening_busy || weight_locked}
                          className="h-12 border-success text-lg font-bold tabular-nums text-success focus-visible:ring-success"
                        />
                        {weight_locked ? (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={onUnlockWeight}
                            disabled={opening_busy}
                            aria-label={t('doctor_pos.weight_edit')}
                            title={t('doctor_pos.weight_edit')}
                            className="border-success text-success hover:bg-success/15 hover:text-success"
                          >
                            <Pencil className="h-4 w-4" aria-hidden />
                          </Button>
                        ) : (
                          <span
                            className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-success/60 bg-success/15 text-success"
                            title={t('doctor_pos.weight_capture')}
                          >
                            <Lock className="h-4 w-4" aria-hidden />
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">{t('cashier_pos.consulting_room')}</Label>
                      <select
                        className="border-input bg-background h-10 w-full rounded-md border px-3 text-sm"
                        value={consulting_room_id_draft}
                        onChange={(e) => setConsultingRoomIdDraft(e.target.value)}
                        disabled={opening_busy}
                      >
                        <option value="">{t('cashier_pos.select_consulting_room')}</option>
                        {rooms_for_selected_branch.map((room) => (
                          <option key={room.id} value={room.id}>
                            {room.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">{t('cashier_pos.consult_reason')}</Label>
                    <textarea
                      className="border-input bg-background min-h-20 w-full rounded-md border px-3 py-2 text-sm"
                      value={cashier_notes_draft}
                      onChange={(e) => setCashierNotesDraft(e.target.value)}
                      placeholder={t('cashier_pos.consult_reason_placeholder')}
                      disabled={opening_busy}
                    />
                  </div>

                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">
                        {t('cashier_pos.provisional_lines')}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <ul className="divide-border divide-y text-sm">
                        {opening_selected_items.map((it) => (
                          <li key={it.id} className="flex items-center justify-between gap-2 py-2">
                            <div className="min-w-0">
                              <p className="truncate font-medium">
                                <span className="font-mono text-xs">{it.item_code}</span> -{' '}
                                {it.item_name}
                              </p>
                              <p className="text-muted-foreground font-mono text-xs">
                                {formatCentsString(it.unit_price_cents)}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  void onUpdateOpeningLineQty(
                                    it.id,
                                    adjustQuantityByUnit(it.quantity, -1),
                                  );
                                }}
                                disabled={opening_busy}
                              >
                                -
                              </Button>
                              <span className="w-14 text-center font-mono text-xs">
                                {it.quantity}
                              </span>
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  void onUpdateOpeningLineQty(
                                    it.id,
                                    adjustQuantityByUnit(it.quantity, 1),
                                  );
                                }}
                                disabled={opening_busy}
                              >
                                +
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  void onRemoveOpeningLine(it.id);
                                }}
                                disabled={opening_busy}
                              >
                                <X className="h-4 w-4" aria-hidden />
                              </Button>
                            </div>
                          </li>
                        ))}
                        {opening_selected_items.length === 0 ? (
                          <li className="text-muted-foreground py-2 text-xs">
                            {t('doctor_pos.lines_empty')}
                          </li>
                        ) : null}
                      </ul>
                      <SellableSearchPanel
                        onAdd={onAddOpeningLine}
                        disabled={opening_busy}
                        allowed_kinds={['service', 'kit']}
                        hide_internal_services
                        preview_context={null}
                      />
                      <div className="border-border flex items-center justify-between border-t pt-2 font-semibold">
                        <span>{t('cashier_pos.total')}</span>
                        <span className="font-mono">{formatCentsString(selected.total_cents)}</span>
                      </div>
                    </CardContent>
                    <CardFooter className="border-border/60 flex justify-end border-t pt-4">
                      <Button
                        type="button"
                        size="lg"
                        onClick={() => void onSendToDoctor()}
                        disabled={opening_busy}
                        className="min-w-44"
                      >
                        {t('cashier_pos.send_to_doctor')}
                      </Button>
                    </CardFooter>
                  </Card>
                </CardContent>
              </Card>
            ) : (
              <Card className="border-border bg-card shadow-sm">
                <CardHeader className="flex flex-row items-start justify-between gap-2">
                  <div>
                    <p className="text-muted-foreground text-xs font-medium uppercase tracking-wide">
                      {selected.consulting_room_name ?? t('cashier_pos.no_consulting_room')}
                    </p>
                    <CardTitle className="text-base">
                      {customerLabel(selected.customer_snapshot_json)} -{' '}
                      {petLabel(selected.pet_snapshot_json)}
                    </CardTitle>
                    <p className="text-muted-foreground text-xs">
                      {t('cashier_pos.items')}: {selected.items.length} -{' '}
                      {dateTimeLabel(selected.sent_to_cashier_at ?? selected.created_at)}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {selected_is_chargeable ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setShowReturn(true)}
                        disabled={charging || returning}
                      >
                        <Undo2 className="h-4 w-4" aria-hidden />
                        <span>{t('cashier_pos.return_to_doctor')}</span>
                      </Button>
                    ) : null}
                    {is_admin && selected.status !== 'sent_to_cashier_for_charge' ? (
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={() => setShowCancel(true)}
                        disabled={charging || returning}
                      >
                        <Ban className="h-4 w-4" aria-hidden />
                        <span>{t('cashier_pos.cancel_sale')}</span>
                      </Button>
                    ) : null}
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {selected.clinical_visit_id ? (
                    <div className="flex items-center gap-2">
                      <CashierClinicalAdvisory sale_order_id={selected.id} />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setShowClinicalModal(true)}
                      >
                        <FileText className="h-4 w-4" aria-hidden />
                        <span>{t('cashier_pos.view_clinical_record')}</span>
                      </Button>
                    </div>
                  ) : null}
                  <ul className="divide-border divide-y text-sm">
                    {grouped_selected_items.map((group, index) => (
                      <li key={`selected-kit-group-${index}`} className="py-1">
                        {group.kit_label ? (
                          <p className="text-muted-foreground mb-1 text-xs font-semibold uppercase tracking-wide">
                            Kit: {group.kit_label}
                          </p>
                        ) : null}
                        <ul className="space-y-1">
                          {group.items.map((it) => (
                            <li
                              key={it.id}
                              className="flex items-center justify-between gap-2 py-1"
                            >
                              <span>
                                <span className="font-mono text-xs">{it.item_code}</span> -{' '}
                                {group.kit_label ? kitComponentLabel(it.item_name) : it.item_name}
                                {(() => {
                                  const lines = kitBreakdownDisplayRows(it, kit_breakdowns);
                                  return lines.length > 0 ? (
                                    <span className="text-muted-foreground mt-1 block text-xs">
                                      {lines.join(' | ')}
                                    </span>
                                  ) : null;
                                })()}
                                {BigInt(it.discount_cents) > 0n ? (
                                  <span className="ml-2 font-mono text-xs tabular-nums text-success">
                                    -{formatCentsString(it.discount_cents)}
                                  </span>
                                ) : null}
                              </span>
                              <span className="flex items-center gap-2">
                                <span className="text-muted-foreground font-mono text-xs">
                                  {it.quantity} x {formatCentsString(it.unit_price_cents)}
                                </span>
                              </span>
                            </li>
                          ))}
                        </ul>
                      </li>
                    ))}
                  </ul>
                  {selected_is_chargeable ? (
                    <PaymentPanel
                      order_id={selected.id}
                      total_cents={selected.total_cents}
                      subtotal_cents={selected.subtotal_cents}
                      discount_cents={selected.discount_cents}
                      tax_cents={selected.tax_cents}
                      charging={charging}
                      applying_discount={applying_discount}
                      on_charge={onCharge}
                      on_apply_discount={async (input) => {
                        if (!selected) return;
                        setErrorText(null);
                        try {
                          await applyCashierOrderDiscount({
                            variables: {
                              input: {
                                sale_order_id: selected.id,
                                discount_cents: input.discount_cents,
                                otp_plaintext: input.otp_plaintext,
                                reason: input.reason,
                              },
                            },
                            context: {
                              headers: {
                                'Idempotency-Key':
                                  typeof crypto !== 'undefined' && 'randomUUID' in crypto
                                    ? crypto.randomUUID()
                                    : `np-${Date.now()}-${Math.random().toString(36).slice(2)}`,
                              },
                            },
                          });
                          await refetchSelected({ id: selected.id });
                          await refetch();
                        } catch (err) {
                          applyApolloErrorMessage(err, t, setErrorText);
                        }
                      }}
                      on_cancel={onClickClearSelection}
                      disabled={!has_open_shift}
                      disabled_reason={!has_open_shift ? no_shift_banner_text : null}
                      open_account_available={Boolean(selected.customer_id && selected.pet_id)}
                    />
                  ) : (
                    <p className="text-muted-foreground text-xs">
                      {t('cashier_pos.not_ready_to_charge')}
                    </p>
                  )}
                </CardContent>
              </Card>
            )
          ) : (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">{t('cashier_pos.no_selection_title')}</CardTitle>
              </CardHeader>
              <CardContent className="text-muted-foreground text-sm">
                {t('cashier_pos.no_selection_body')}
              </CardContent>
            </Card>
          )}
          <p className="text-muted-foreground text-xs">{t('cashier_pos.shortcuts_hint')}</p>
        </div>
      </div>

      <section className="space-y-3">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-muted-foreground text-sm font-semibold uppercase tracking-wide">
            {t('cashier_pos.shift_history_title')} - {history_entries.length}
          </h3>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              if (active_shift_id) void refetchHistory();
            }}
            disabled={!active_shift_id}
            aria-label={t('cashier_pos.refresh_history')}
          >
            <RefreshCw className="h-4 w-4" aria-hidden />
          </Button>
        </div>
        {history_entries.length === 0 ? (
          <Card>
            <CardContent className="text-muted-foreground py-6 text-center text-sm">
              {t('cashier_pos.shift_history_empty')}
            </CardContent>
          </Card>
        ) : (
          <div className="overflow-hidden rounded-md border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-muted-foreground text-xs uppercase tracking-wide">
                <tr>
                  <th className="px-3 py-2 text-left">{t('cashier_pos.history_time')}</th>
                  <th className="px-3 py-2 text-left">{t('cashier_pos.history_ticket')}</th>
                  <th className="px-3 py-2 text-right">{t('cashier_pos.history_amount')}</th>
                </tr>
              </thead>
              <tbody className="divide-border divide-y">
                {history_entries.map((entry) => (
                  <tr
                    key={entry.sale_order_id}
                    className="hover:bg-accent/10 cursor-pointer"
                    onClick={() => setHistoryDetail(entry)}
                  >
                    <td className="px-3 py-2">
                      <span className="inline-flex items-center gap-2">
                        <Clock3 className="text-muted-foreground h-4 w-4" aria-hidden />
                        {timeLabel(entry.paid_at)}
                      </span>
                    </td>
                    <td className="px-3 py-2 font-mono">
                      {ticketLabel(entry.ticket_folio_number, entry.sale_folio_number)}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">
                      {formatCentsString(entry.total_cents)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {selected?.clinical_visit_id ? (
        <ClinicalVisitModal
          sale_order_id={selected.id}
          open={show_clinical_modal}
          onOpenChange={setShowClinicalModal}
        />
      ) : null}

      {show_return && selected ? (
        <ReturnToDoctorModal
          submitting={returning}
          on_close={() => setShowReturn(false)}
          on_submit={onSubmitReturn}
        />
      ) : null}

      {show_cancel && selected ? (
        <CancelSaleDialog
          sale_order_id={selected.id}
          on_close={() => setShowCancel(false)}
          on_done={async () => {
            setShowCancel(false);
            setSelectedId(null);
            await refetch();
          }}
          on_error={setErrorText}
        />
      ) : null}

      {show_request_cfdi && last_paid_sale ? (
        <RequestCfdiDialog
          sale_order_id={last_paid_sale.id}
          customer_id={last_paid_sale.customer_id}
          on_close={() => setShowRequestCfdi(false)}
          on_done={async () => {
            setShowRequestCfdi(false);
          }}
          on_error={setErrorText}
        />
      ) : null}

      {open_account_detail_id ? (
        <OpenAccountDetailDialog
          account_id={open_account_detail_id}
          on_close={() => {
            setOpenAccountDetailId(null);
            void refetchOpenAccounts();
          }}
        />
      ) : null}

      <ConfirmDialog
        open={show_discard_confirm}
        title={t('cashier_pos.discard_opening')}
        description={t('cashier_pos.discard_opening_confirm')}
        confirm_label={t('cashier_pos.discard_opening')}
        cancel_label={t('common.cancel')}
        loading_label={t('common.loading')}
        loading={discarding_opening}
        destructive
        on_confirm={() => {
          if (!discard_target_id) return;
          void onDiscardOpeningOrder(discard_target_id);
        }}
        on_cancel={() => {
          if (discarding_opening) return;
          setShowDiscardConfirm(false);
          setDiscardTargetId(null);
        }}
      />

      {history_detail ? (
        <div className="bg-background/80 fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
          <Card className="max-h-[90vh] w-full max-w-2xl overflow-auto">
            <CardHeader className="flex flex-row items-start justify-between gap-2">
              <div>
                <CardTitle className="text-base">
                  {t('cashier_pos.history_detail_title')}{' '}
                  {ticketLabel(
                    history_detail.ticket_folio_number,
                    history_detail.sale_folio_number,
                  )}
                </CardTitle>
                <p className="text-muted-foreground text-xs">
                  {dateTimeLabel(history_detail.paid_at)} -{' '}
                  {history_detail.consulting_room_name ?? t('cashier_pos.no_consulting_room')}
                </p>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  onClick={() =>
                    void loadMissingKitBreakdowns(history_detail.sale_order.items).then(
                      (resolved_breakdowns) =>
                        printSaleTicket(history_detail.sale_order, {
                          mode: 'reprint',
                          cashier_name,
                          ticket_number: ticketLabel(
                            history_detail.ticket_folio_number,
                            history_detail.sale_folio_number,
                          ),
                          kit_breakdowns: resolved_breakdowns,
                          payment_methods: history_detail.payment_methods,
                          payment_method_summary: history_detail.payment_method_summary,
                        }),
                    )
                  }
                  aria-label={t('cashier_pos.print_ticket')}
                >
                  <Printer className="h-4 w-4" aria-hidden />
                </Button>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  onClick={() => setHistoryDetail(null)}
                  aria-label={t('common.cancel')}
                >
                  <X className="h-4 w-4" aria-hidden />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-2 text-sm md:grid-cols-2">
                <div>
                  <p className="text-muted-foreground text-xs uppercase tracking-wide">Tutor</p>
                  <p className="font-medium">
                    {history_detail.tutor_name ??
                      customerLabel(history_detail.customer_snapshot_json)}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs uppercase tracking-wide">Mascota</p>
                  <p className="font-medium">
                    {history_detail.pet_name ?? petLabel(history_detail.pet_snapshot_json)}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs uppercase tracking-wide">
                    {t('cashier_pos.payment_title')}
                  </p>
                  <p className="font-medium">
                    {history_detail.payment_method_summary ?? 'Pago registrado'}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs uppercase tracking-wide">
                    {t('cashier_pos.total')}
                  </p>
                  <p className="font-mono font-medium">
                    {formatCentsString(history_detail.total_cents)}
                  </p>
                </div>
              </div>
              {history_detail.payment_methods.length > 0 ? (
                <div className="border-border bg-muted/20 rounded-md border p-3 text-sm">
                  <p className="text-muted-foreground mb-2 text-xs uppercase tracking-wide">
                    Método de pago
                  </p>
                  <ul className="space-y-2">
                    {history_detail.payment_methods.map((payment, index) => (
                      <li
                        key={`${history_detail.sale_order_id}-${payment.payment_method_id}-${payment.amount_cents}-${index}`}
                        className="space-y-0.5"
                      >
                        <div className="flex items-center justify-between gap-3">
                          <span className="font-medium">{payment.payment_method_name}</span>
                          <span className="font-mono">
                            {formatCentsString(payment.amount_cents)}
                          </span>
                        </div>
                        {payment.reference_number ? (
                          <p className="text-muted-foreground text-xs">
                            Referencia: {payment.reference_number}
                          </p>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              <ul className="divide-border divide-y rounded-md border text-sm">
                {groupItemsByKit(history_detail.sale_order.items).map((group, index) => (
                  <li key={`history-kit-group-${index}`} className="px-3 py-2">
                    {group.kit_label ? (
                      <p className="text-muted-foreground mb-1 text-xs font-semibold uppercase tracking-wide">
                        Kit: {group.kit_label}
                      </p>
                    ) : null}
                    <ul className="space-y-1">
                      {group.items.map((item) => (
                        <li key={item.id} className="flex items-center justify-between gap-3">
                          <span>
                            <span className="text-muted-foreground font-mono text-xs">
                              {item.item_code}
                            </span>{' '}
                            {group.kit_label ? kitComponentLabel(item.item_name) : item.item_name}
                            {(() => {
                              const lines = kitBreakdownDisplayRows(item, kit_breakdowns);
                              return lines.length > 0 ? (
                                <span className="text-muted-foreground mt-1 block text-xs">
                                  {lines.join(' | ')}
                                </span>
                              ) : null;
                            })()}
                          </span>
                          <span className="font-mono">{formatCentsString(item.total_cents)}</span>
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
              <div className="flex items-center justify-between text-sm font-semibold">
                <span>{t('cashier_pos.total')}</span>
                <span className="font-mono">{formatCentsString(history_detail.total_cents)}</span>
              </div>
            </CardContent>
          </Card>
        </div>
      ) : null}
    </div>
  );
}

